import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { X } from "lucide-react";

const inputCls =
  "w-full bg-[#0D0E13] border border-white/10 rounded-2xl px-5 py-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/70 transition-colors duration-300";

export function AuthModal({ open, onClose, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user =
        mode === "login"
          ? await login(form.email, form.password)
          : await register(form.name, form.email, form.password);
      onSuccess?.(user);
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Accesso non riuscito. Controlla i dati."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="auth-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#13141B] border border-white/10 rounded-3xl p-8 relative"
            data-testid="auth-modal"
          >
            <button onClick={onClose} className="absolute top-5 right-5 text-slate-500 hover:text-white transition-colors" data-testid="auth-modal-close-button" aria-label="Chiudi">
              <X size={20} />
            </button>

            <p className="font-mono-adj text-[10px] uppercase tracking-[0.3em] text-orange-500 font-semibold">Area Clienti</p>
            <h3 className="font-heading text-2xl font-extrabold text-white mt-2">
              {mode === "login" ? "Bentornato" : "Crea il tuo account"}
            </h3>

            <div className="flex bg-[#0D0E13] border border-white/10 rounded-full p-1 mt-6" data-testid="auth-mode-tabs">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  onClick={() => {
                    setMode(m);
                    setError("");
                  }}
                  data-testid={`auth-tab-${m}`}
                  className={`flex-1 rounded-full py-2.5 font-mono-adj text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                    mode === m ? "fire-bg text-white font-semibold" : "text-slate-400 hover:text-white"
                  }`}
                >
                  {m === "login" ? "Accedi" : "Registrati"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4 mt-6" data-testid="auth-form">
              {mode === "register" && (
                <input required placeholder="Nome e Cognome" value={form.name} onChange={set("name")} className={inputCls} data-testid="register-name-input" />
              )}
              <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="login-modal-email-input" />
              {mode === "register" && (
                <p className="text-[11px] text-slate-500 -mt-2 px-1" data-testid="register-email-hint">
                  Usa la tua email personale: ti invieremo qui le conferme di prenotazione e gli aggiornamenti sui tuoi lavaggi.
                </p>
              )}
              <input
                required
                type="password"
                minLength={6}
                placeholder="Password (min. 6 caratteri)"
                value={form.password}
                onChange={set("password")}
                className={inputCls}
                data-testid="login-modal-password-input"
              />
              {error && (
                <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="auth-error-message">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-modal-submit-button"
                className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:opacity-90 active:scale-95 transition-all duration-300 disabled:opacity-50"
              >
                {loading ? "Attendere..." : mode === "login" ? "Accedi" : "Crea Account"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
